/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import './App.css';
import Docs from './Docs';
import Voting from './Voting';
import NFT from './NFT';

// ============== CONFIGURATION ==============
const CHAIN_ID = 143n;
const CHAIN_NAME = 'Monad Mainnet';
const RPC_URL = 'https://rpc1.monad.xyz';

const POOLS = {
  500: '0x38847b47dD95a97B48F480c20B988b050Cdc80Ab',
  5000: '0x4C5F83411AfA37e11DD274C8bFa2B7a052A4D3e4',
  50000: '0x273fFc1ab2873b8A1A7cc73dAF92eCc144C67a2e',
  500000: '0xBc8C4062B8e898C2AeE7f0AF686cE3C189392E22',
  5000000: '0xd063fdF34a389557bB8E6b94343d7F6BBB66776e'
};

const COMPLIANCE_ADDRESS = '0x9B8b20ad3F56F7b45f4d6ea6845cC87410732473';
const GHOST_ADDRESS = '0x601CE35D2aD556599F796323c98b25a8d5D17777';
const VERSION = '1.0.6';
const AMOUNTS = [500, 5000, 50000, 500000, 5000000];
const MERKLE_TREE_HEIGHT = 20;
const DEPOSIT_START_BLOCK = 47890000;

// ============== ABIs ==============
const PayrollPoolABI = [
  "function deposit(bytes32 _commitment) external payable",
  "function withdraw(uint[2] calldata _pA, uint[2][2] calldata _pB, uint[2] calldata _pC, bytes32 _root, bytes32 _nullifierHash, address payable _recipient, address payable _relayer, uint256 _relayerFee, uint256 _refund) external payable",
  "function denomination() view returns (uint256)",
  "function getBalance() view returns (uint256)",
  "function nextIndex() view returns (uint32)",
  "function getLastRoot() view returns (bytes32)",
  "function isSpent(bytes32 _nullifierHash) view returns (bool)",
  "function isKnownRoot(bytes32 _root) view returns (bool)",
  "function commitments(bytes32) view returns (bool)",
  "event Deposit(bytes32 indexed commitment, uint32 leafIndex, uint256 timestamp)"
];

const ComplianceABI = [
  "function createReport(bytes32 _nullifierHash, bytes32 _commitment, uint256 _amount) external",
  "function verifyReport(bytes32 _nullifierHash) view returns (bool exists, address depositor, uint256 depositTime, uint256 amount)"
];

// ============== POSEIDON ==============
let poseidon;
let poseidonPromise = null;

const initPoseidon = async () => {
  if (poseidon) return poseidon;
  if (poseidonPromise) return poseidonPromise;
  poseidonPromise = (async () => {
    const { buildPoseidon } = await import('circomlibjs');
    poseidon = await buildPoseidon();
    return poseidon;
  })();
  return poseidonPromise;
};

const poseidonHash = async (inputs) => {
  const p = await initPoseidon();
  const hash = p(inputs.map(x => BigInt(x)));
  return p.F.toString(hash);
};

const poseidonHash2 = async (left, right) => poseidonHash([left, right]);

// ============== MERKLE TREE ==============
class MerkleTree {
  constructor(height) { this.height = height; this.leaves = []; this.layers = []; this.zeroValues = []; }

  async initialize() {
    this.zeroValues = [BigInt(0)];
    for (let i = 1; i <= this.height; i++) {
      const prevZero = this.zeroValues[i - 1];
      this.zeroValues[i] = BigInt(await poseidonHash2(prevZero.toString(), prevZero.toString()));
    }
  }

  async rebuild(leaves) {
    this.leaves = leaves.map(l => BigInt(l));
    this.layers = [this.leaves.slice()];
    for (let level = 0; level < this.height; level++) {
      const currentLayer = this.layers[level];
      const nextLayer = [];
      for (let i = 0; i < Math.pow(2, this.height - level); i += 2) {
        const left = i < currentLayer.length ? currentLayer[i] : this.zeroValues[level];
        const right = i + 1 < currentLayer.length ? currentLayer[i + 1] : this.zeroValues[level];
        nextLayer.push(BigInt(await poseidonHash2(left.toString(), right.toString())));
      }
      this.layers.push(nextLayer);
    }
  }

  getRoot() {
    if (this.layers.length === 0) return this.zeroValues[this.height];
    return this.layers[this.layers.length - 1][0] || this.zeroValues[this.height];
  }

  getPathElements(leafIndex) {
    const pathElements = [], pathIndices = [];
    let currentIndex = leafIndex;
    for (let level = 0; level < this.height; level++) {
      const siblingIndex = currentIndex % 2 === 0 ? currentIndex + 1 : currentIndex - 1;
      const currentLayer = this.layers[level] || [];
      const sibling = siblingIndex < currentLayer.length ? currentLayer[siblingIndex] : this.zeroValues[level];
      pathElements.push(sibling.toString());
      pathIndices.push(currentIndex % 2);
      currentIndex = Math.floor(currentIndex / 2);
    }
    return { pathElements, pathIndices };
  }
}

// ============== ICONS ==============
const GhostLogo = () => <img src="ghost.png" alt="Ghost Protocol" width="40" height="40" style={{ borderRadius: '8px' }} />;
const MonadLogo = () => <img src="monad-logo.png" alt="Monad" width="20" height="20" style={{ borderRadius: '4px' }} />;
const XLogo = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
const TelegramIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>;

// ============== STORAGE ==============
const DEPOSITS_STORAGE_KEY = 'monado_deposits';
const DEPOSITS_CACHE_KEY = 'ghost_deposits_cache';

const saveDepositsToStorage = (deposits) => { try { localStorage.setItem(DEPOSITS_STORAGE_KEY, JSON.stringify(deposits)); } catch (e) { } };
const loadDepositsFromStorage = () => { try { return JSON.parse(localStorage.getItem(DEPOSITS_STORAGE_KEY)) || {}; } catch (e) { return {}; } };
const saveDepositsCache = (poolAddress, deposits, lastBlock) => { try { const cache = JSON.parse(localStorage.getItem(DEPOSITS_CACHE_KEY) || '{}'); cache[poolAddress] = { deposits, lastBlock, timestamp: Date.now() }; localStorage.setItem(DEPOSITS_CACHE_KEY, JSON.stringify(cache)); } catch (e) { } };
const loadDepositsCache = (poolAddress) => { try { return JSON.parse(localStorage.getItem(DEPOSITS_CACHE_KEY) || '{}')[poolAddress] || null; } catch (e) { return null; } };

// ============== COMPLIANCE CERTIFICATE ==============
const generateComplianceCertificate = (data) => `
════════════════════════════════════════════════════════════════════════════════
                    GHOST PROTOCOL COMPLIANCE CERTIFICATE                        
════════════════════════════════════════════════════════════════════════════════

Certificate ID: ${data.certificateId}
Generated:      ${data.reportDate}

────────────────────────────────────────────────────────────────────────────────
DEPOSITOR INFORMATION
────────────────────────────────────────────────────────────────────────────────

Wallet Address:    ${data.depositor}
Deposit Amount:    ${data.amount} MON
Pool Contract:     ${data.poolAddress}

────────────────────────────────────────────────────────────────────────────────
CRYPTOGRAPHIC PROOF
────────────────────────────────────────────────────────────────────────────────

Commitment Hash:
${data.commitment}

Nullifier Hash:
${data.nullifierHash}

────────────────────────────────────────────────────────────────────────────────
ON-CHAIN RECORD
────────────────────────────────────────────────────────────────────────────────

Transaction Hash:     ${data.txHash}
Compliance Contract:  ${COMPLIANCE_ADDRESS}

────────────────────────────────────────────────────────────────────────────────
VERIFICATION INSTRUCTIONS
────────────────────────────────────────────────────────────────────────────────

This certificate proves that the wallet address above made a deposit to 
Ghost Protocol. To verify this certificate:

1. Go to https://monadscan.com
2. Navigate to the Compliance Contract: ${COMPLIANCE_ADDRESS}
3. Call the verifyReport() function with the Nullifier Hash above
4. The function will return the depositor address, timestamp, and amount

Network: Monad Mainnet (Chain ID: 143)

════════════════════════════════════════════════════════════════════════════════
                           CERTIFICATE AUTHENTICITY                              
════════════════════════════════════════════════════════════════════════════════

This compliance certificate was generated by Ghost Protocol's on-chain 
compliance system. The information is cryptographically linked to blockchain 
transactions and can be independently verified by any third party.

For questions or support: https://t.me/GhostProtocolHQ

Generated by Ghost Protocol v${VERSION}
════════════════════════════════════════════════════════════════════════════════
`;

const downloadComplianceCertificate = (certificate, amount) => {
  const blob = new Blob([certificate], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ghost-compliance-certificate-${amount}MON-${Date.now()}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ============== MAIN APP ==============
function App() {
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [activeTab, setActiveTab] = useState('deposit');
  const [selectedAmount, setSelectedAmount] = useState(5000);
  const [note, setNote] = useState('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [showVoting, setShowVoting] = useState(false);
  const [showNFT, setShowNFT] = useState(false);
  const [withdrawStatus, setWithdrawStatus] = useState('');
  const [complianceNote, setComplianceNote] = useState('');
  const [complianceStatus, setComplianceStatus] = useState('');
  const [complianceCertificate, setComplianceCertificate] = useState(null);
  const [stats, setStats] = useState({ anonymitySet: 0, poolBalance: '0', recentDeposits: [] });
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Check if user has seen welcome popup
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('ghost_welcome_seen');
    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
    }
  }, []);

  const dismissWelcome = () => {
    localStorage.setItem('ghost_welcome_seen', 'true');
    setShowWelcomeModal(false);
  };

  useEffect(() => { initPoseidon().catch(() => { }); }, []);

  useEffect(() => {
    const walletProvider = window.phantom?.ethereum || window.ethereum;
    if (!walletProvider) return;
    const handleChainChanged = () => { if (account) window.location.reload(); };
    const handleAccountsChanged = (accounts) => { if (accounts?.length > 0) setAccount(accounts[0]); else disconnectWallet(); };
    walletProvider.on('chainChanged', handleChainChanged);
    walletProvider.on('accountsChanged', handleAccountsChanged);
    return () => { walletProvider.removeListener('chainChanged', handleChainChanged); walletProvider.removeListener('accountsChanged', handleAccountsChanged); };
  }, [account]);

  useEffect(() => {
    let mounted = true;
    const checkConnection = async () => {
      const walletProvider = window.phantom?.ethereum || window.ethereum;
      if (walletProvider && mounted) {
        try {
          const accounts = await walletProvider.request({ method: "eth_accounts" });
          if (accounts?.length > 0 && mounted) {
            const ethersProvider = new ethers.BrowserProvider(walletProvider);
            const network = await ethersProvider.getNetwork();
            if (network.chainId === CHAIN_ID && mounted) {
              const ethSigner = await ethersProvider.getSigner();
              setAccount(accounts[0]);
              setProvider(ethersProvider);
              setSigner(ethSigner);
            }
          }
        } catch (e) { }
      }
    };
    checkConnection();
    return () => { mounted = false; };
  }, []);

  useEffect(() => { fetchStats(selectedAmount, null); }, []);

  const getPoolContract = useCallback((amount, signerOrProvider) => {
    const address = POOLS[amount];
    if (!address) return null;
    return new ethers.Contract(address, PayrollPoolABI, signerOrProvider);
  }, []);

  const fetchStats = useCallback(async (amount, signerOrProvider) => {
    try {
      const providerToUse = signerOrProvider || new ethers.JsonRpcProvider(RPC_URL);
      const contract = getPoolContract(amount, providerToUse);
      if (!contract) return;
      const nextIndex = await contract.nextIndex();
      const balance = await contract.getBalance();
      setStats({ anonymitySet: Number(nextIndex), poolBalance: ethers.formatEther(balance), recentDeposits: [] });
    } catch (e) { }
  }, [getPoolContract]);

  const fetchAllDeposits = async (poolAddress, setProgress) => {
    try {
      const rpcProvider = new ethers.JsonRpcProvider(RPC_URL);
      const rpcContract = new ethers.Contract(poolAddress, PayrollPoolABI, rpcProvider);
      const currentBlock = await rpcProvider.getBlockNumber();
      const cached = loadDepositsCache(poolAddress);
      let deposits = [];
      let startBlock = DEPOSIT_START_BLOCK;

      if (cached?.deposits && cached.lastBlock) {
        deposits = cached.deposits;
        startBlock = cached.lastBlock + 1;
        if (setProgress) setProgress(`Found ${deposits.length} cached deposits...`);
        if (currentBlock - cached.lastBlock < 100) {
          if (setProgress) setProgress(`Using cached data (${deposits.length} deposits)`);
          return deposits;
        }
      }

      const CHUNK_SIZE = 2000;
      const totalBlocks = currentBlock - startBlock;
      let fromBlock = startBlock;

      while (fromBlock <= currentBlock) {
        const toBlock = Math.min(fromBlock + CHUNK_SIZE, currentBlock);
        const percent = totalBlocks > 0 ? Math.round(((toBlock - startBlock) / totalBlocks) * 100) : 100;
        if (setProgress) setProgress(`Scanning: ${percent}% - ${deposits.length} deposits`);

        try {
          const filter = rpcContract.filters.Deposit();
          const events = await rpcContract.queryFilter(filter, fromBlock, toBlock);
          for (const event of events) {
            const commitment = event.args.commitment;
            if (!deposits.find(d => d.commitment === commitment)) {
              deposits.push({ commitment, leafIndex: Number(event.args.leafIndex), timestamp: Number(event.args.timestamp), blockNumber: event.blockNumber });
            }
          }
        } catch (e) {
          for (let subFrom = fromBlock; subFrom <= toBlock; subFrom += 500) {
            const subTo = Math.min(subFrom + 500, toBlock);
            try {
              const filter = rpcContract.filters.Deposit();
              const events = await rpcContract.queryFilter(filter, subFrom, subTo);
              for (const event of events) {
                const commitment = event.args.commitment;
                if (!deposits.find(d => d.commitment === commitment)) {
                  deposits.push({ commitment, leafIndex: Number(event.args.leafIndex), timestamp: Number(event.args.timestamp), blockNumber: event.blockNumber });
                }
              }
            } catch (e) { }
          }
        }
        fromBlock = toBlock + 1;
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      deposits.sort((a, b) => a.leafIndex - b.leafIndex);
      saveDepositsCache(poolAddress, deposits, currentBlock);
      if (setProgress) setProgress(`Scan complete! Found ${deposits.length} deposits.`);
      return deposits;
    } catch (e) { return []; }
  };

  const connectWallet = async () => {
    try {
      const walletProvider = window.phantom?.ethereum || window.ethereum;
      if (!walletProvider) { setMessage('Please install MetaMask or Phantom wallet!'); return; }
      const accounts = await walletProvider.request({ method: "eth_requestAccounts" });
      if (!accounts?.length) { setMessage('No accounts found'); return; }
      const ethersProvider = new ethers.BrowserProvider(walletProvider);
      const network = await ethersProvider.getNetwork();

      if (network.chainId !== CHAIN_ID) {
        setMessage('Switching to Monad Mainnet...');
        try {
          await walletProvider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0x' + CHAIN_ID.toString(16) }] });
        } catch (switchError) {
          if (switchError.code === 4902) {
            await walletProvider.request({ method: 'wallet_addEthereumChain', params: [{ chainId: '0x' + CHAIN_ID.toString(16), chainName: CHAIN_NAME, nativeCurrency: { name: 'MON', symbol: 'MON', decimals: 18 }, rpcUrls: [RPC_URL], blockExplorerUrls: ['https://monadscan.com'] }] });
          } else { setMessage('Please switch to Monad Mainnet manually'); return; }
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        const newEthersProvider = new ethers.BrowserProvider(walletProvider);
        const newSigner = await newEthersProvider.getSigner();
        const newAccounts = await walletProvider.request({ method: "eth_accounts" });
        setProvider(newEthersProvider); setSigner(newSigner); setAccount(newAccounts[0]); setMessage('');
        await fetchStats(selectedAmount, newSigner);
        return;
      }

      const ethSigner = await ethersProvider.getSigner();
      setAccount(accounts[0]); setProvider(ethersProvider); setSigner(ethSigner); setMessage('');
      await fetchStats(selectedAmount, ethSigner);
    } catch (error) { setMessage(error.code === 4001 ? 'Connection rejected by user' : 'Failed to connect wallet'); }
  };

  const disconnectWallet = () => { setAccount(''); setProvider(null); setSigner(null); setNote(''); setMessage('Wallet disconnected'); setStats({ anonymitySet: 0, poolBalance: '0', recentDeposits: [] }); };

  const generateNote = async () => {
    await initPoseidon();
    const nullifier = ethers.toBigInt(ethers.randomBytes(31));
    const secret = ethers.toBigInt(ethers.randomBytes(31));
    const commitment = await poseidonHash([nullifier.toString(), secret.toString()]);
    const nullifierHash = await poseidonHash([nullifier.toString()]);
    const noteString = `ghost-monad-${selectedAmount}-0x${nullifier.toString(16).padStart(62, '0')}-0x${secret.toString(16).padStart(62, '0')}`;
    return { commitment, noteString, noteData: { nullifier: nullifier.toString(), secret: secret.toString(), commitment, nullifierHash, denomination: selectedAmount } };
  };

  const parseNote = (noteString) => {
    if (noteString.startsWith('ghost-monad-') || noteString.startsWith('monado-monad-')) {
      const parts = noteString.split('-');
      return { denomination: parseInt(parts[2]), nullifier: BigInt(parts[3]).toString(), secret: BigInt(parts[4]).toString() };
    }
    throw new Error('Invalid note format');
  };

  const downloadNote = (noteString, amount) => {
    const blob = new Blob([noteString], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `ghost-backup-${amount}MON-${Date.now()}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const copyNoteToClipboard = async (noteString) => { try { await navigator.clipboard.writeText(noteString); return true; } catch (e) { return false; } };

  const handleDeposit = async () => {
    if (!signer) { setMessage('Connect wallet first'); return; }
    setNote(''); setLoading(true);
    try {
      const contract = getPoolContract(selectedAmount, signer);
      if (!contract) { setMessage('Pool not deployed yet'); setLoading(false); return; }
      setMessage('🔄 Generating note...');
      const { commitment, noteString, noteData } = await generateNote();
      setNote(noteString);
      await copyNoteToClipboard(noteString);
      setMessage('📋 Note copied to clipboard! Save it securely before confirming.');
      const commitmentHex = '0x' + BigInt(commitment).toString(16).padStart(64, '0');
      const tx = await contract.deposit(commitmentHex, { value: ethers.parseEther(selectedAmount.toString()) });
      setMessage('⏳ Transaction submitted, waiting for confirmation...');
      const receipt = await tx.wait();
      const deposits = loadDepositsFromStorage();
      const poolKey = `${POOLS[selectedAmount]}_${selectedAmount}`;
      if (!deposits[poolKey]) deposits[poolKey] = [];
      const depositEvent = receipt.logs.find(log => { try { return contract.interface.parseLog(log).name === 'Deposit'; } catch { return false; } });
      let leafIndex = deposits[poolKey].length;
      if (depositEvent) leafIndex = Number(contract.interface.parseLog(depositEvent).args.leafIndex);
      deposits[poolKey].push({ commitment: commitmentHex, nullifier: noteData.nullifier, secret: noteData.secret, nullifierHash: noteData.nullifierHash, leafIndex, timestamp: Date.now() });
      saveDepositsToStorage(deposits);
      setMessage('✅ Deposit successful! Make sure you saved your note!');
      await fetchStats(selectedAmount, signer);
    } catch (error) { setMessage(note ? '❌ Transaction failed. YOUR NOTE IS STILL VALID! Error: ' + (error.reason || error.message) : '❌ ' + (error.reason || error.message)); }
    setLoading(false);
  };

  const handleWithdraw = async () => {
    if (!withdrawNote) { setMessage('Please enter your note'); return; }
    if (!recipientAddress) { setMessage('Please enter recipient address'); return; }
    if (!ethers.isAddress(recipientAddress)) { setMessage('Invalid recipient address'); return; }
    if (!signer) { setMessage('Connect wallet first'); return; }
    setLoading(true); setWithdrawStatus('Parsing note...');
    try {
      const noteData = parseNote(withdrawNote);
      const contract = getPoolContract(noteData.denomination, signer);
      if (!contract) { setMessage('❌ Pool not found'); setLoading(false); setWithdrawStatus(''); return; }
      setWithdrawStatus('Calculating hashes...');
      const commitment = await poseidonHash([noteData.nullifier, noteData.secret]);
      const nullifierHash = await poseidonHash([noteData.nullifier]);
      const commitmentHex = '0x' + BigInt(commitment).toString(16).padStart(64, '0');
      const nullifierHashHex = '0x' + BigInt(nullifierHash).toString(16).padStart(64, '0');
      if (await contract.isSpent(nullifierHashHex)) { setMessage('❌ This note has already been spent!'); setLoading(false); setWithdrawStatus(''); return; }
      if (!(await contract.commitments(commitmentHex))) { setMessage('❌ Commitment not found.'); setLoading(false); setWithdrawStatus(''); return; }
      const allDeposits = await fetchAllDeposits(POOLS[noteData.denomination], setWithdrawStatus);
      if (!allDeposits.length) { setMessage('❌ No deposits found'); setLoading(false); setWithdrawStatus(''); return; }
      const ourDepositIndex = allDeposits.findIndex(d => d.commitment.toLowerCase() === commitmentHex.toLowerCase());
      if (ourDepositIndex === -1) { setMessage('❌ Your deposit was not found'); setLoading(false); setWithdrawStatus(''); return; }
      setWithdrawStatus('Building merkle tree...');
      const tree = new MerkleTree(MERKLE_TREE_HEIGHT);
      await tree.initialize();
      await tree.rebuild(allDeposits.map(d => BigInt(d.commitment).toString()));
      const root = tree.getRoot();
      const rootHex = '0x' + root.toString(16).padStart(64, '0');
      if (!(await contract.isKnownRoot(rootHex))) { setMessage('❌ Merkle root not recognized.'); setLoading(false); setWithdrawStatus(''); return; }
      const { pathElements, pathIndices } = tree.getPathElements(ourDepositIndex);
      setWithdrawStatus('Generating ZK proof (30-60 seconds)...');
      const input = { root: root.toString(), nullifierHash, recipient: BigInt(recipientAddress).toString(), relayer: '0', fee: '0', refund: '0', nullifier: noteData.nullifier, secret: noteData.secret, pathElements, pathIndices };
      const snarkjs = await import('snarkjs');
      const { proof } = await snarkjs.groth16.fullProve(input, '/circuits/withdraw.wasm', '/circuits/withdraw_final.zkey');
      setWithdrawStatus('Proof generated! Submitting transaction...');
      const pA = [proof.pi_a[0], proof.pi_a[1]];
      const pB = [[proof.pi_b[0][1], proof.pi_b[0][0]], [proof.pi_b[1][1], proof.pi_b[1][0]]];
      const pC = [proof.pi_c[0], proof.pi_c[1]];
      const tx = await contract.withdraw(pA, pB, pC, rootHex, nullifierHashHex, recipientAddress, ethers.ZeroAddress, 0, 0);
      setWithdrawStatus('Waiting for confirmation...');
      await tx.wait();
      setMessage('✅ Withdrawal successful! Funds sent to ' + recipientAddress.slice(0, 10) + '...');
      setWithdrawNote(''); setRecipientAddress('');
      await fetchStats(noteData.denomination, signer);
    } catch (error) { setMessage('❌ Withdrawal failed: ' + (error.reason || error.message)); }
    setWithdrawStatus(''); setLoading(false);
  };

  const handleGenerateCompliance = async () => {
    if (!signer) { setComplianceStatus('❌ Connect wallet first'); return; }
    if (!complianceNote) { setComplianceStatus('❌ Please enter your note'); return; }
    setComplianceStatus('Parsing note...'); setComplianceCertificate(null);
    try {
      const noteData = parseNote(complianceNote);
      setComplianceStatus('Calculating hashes...');
      const nullifierHash = await poseidonHash([noteData.nullifier]);
      const commitment = await poseidonHash([noteData.nullifier, noteData.secret]);
      const nullifierHashHex = '0x' + BigInt(nullifierHash).toString(16).padStart(64, '0');
      const commitmentHex = '0x' + BigInt(commitment).toString(16).padStart(64, '0');
      const poolAddress = POOLS[noteData.denomination];
      const pool = new ethers.Contract(poolAddress, PayrollPoolABI, signer);
      if (!(await pool.commitments(commitmentHex))) { setComplianceStatus('❌ Deposit not found. This note may be invalid.'); return; }
      setComplianceStatus('Creating on-chain report...');
      const compliance = new ethers.Contract(COMPLIANCE_ADDRESS, ComplianceABI, signer);
      const tx = await compliance.createReport(nullifierHashHex, commitmentHex, ethers.parseEther(noteData.denomination.toString()));
      setComplianceStatus('Waiting for confirmation...');
      const receipt = await tx.wait();
      setComplianceStatus('Generating certificate...');
      const certificate = generateComplianceCertificate({ certificateId: `GHOST-${Date.now().toString(36).toUpperCase()}`, depositor: account, amount: noteData.denomination.toLocaleString(), poolAddress, commitment: commitmentHex, nullifierHash: nullifierHashHex, reportDate: new Date().toISOString(), txHash: receipt.hash });
      setComplianceCertificate(certificate);
      setComplianceStatus('✅ Compliance report created successfully!');
    } catch (e) { setComplianceStatus('❌ ' + (e.reason || e.message || 'Failed to create report')); }
  };

  useEffect(() => { fetchStats(selectedAmount, signer); }, [selectedAmount, signer, fetchStats]);

  const formatAmount = (amt) => amt >= 1000000 ? (amt / 1000000) + 'M' : amt >= 1000 ? (amt / 1000) + 'K' : amt.toString();

  if (showDocs) return <Docs onBack={() => setShowDocs(false)} />;
  if (showVoting) return <Voting onBack={() => setShowVoting(false)} signer={signer} />;
  if (showNFT) return <NFT onBack={() => setShowNFT(false)} />;

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <div className="logo"><GhostLogo /><span className="logo-text">Ghost Protocol</span></div>
          <nav className="nav">
            <a href="https://nad.fun/tokens/0x601CE35D2aD556599F796323c98b25a8d5D17777" target="_blank" rel="noopener noreferrer" className="nav-link buy-link">BUY $GHOST</a>
            <a href="https://playghosted.xyz/" target="_blank" rel="noopener noreferrer" className="nav-link">Game</a>
            <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setShowVoting(true); }}>Voting</a>
            <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setShowNFT(true); }}>NFT</a>
            <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setShowComplianceModal(true); }}>Compliance</a>
            <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setShowDocs(true); }}>Docs</a>
          </nav>
        </div>
        <div className="header-right">
          <div className="network-selector"><MonadLogo /><span>Monad</span></div>
          {account ? (
            <div className="wallet-section">
              <div className="wallet-badge">{account.slice(0, 6)}...{account.slice(-4)}</div>
              <button className="disconnect-btn" onClick={disconnectWallet}>Disconnect</button>
            </div>
          ) : (
            <button className="connect-btn" onClick={connectWallet}>Connect</button>
          )}
        </div>
      </header>

      <main className="main">
        <div className="content-wrapper">
          <div className="card main-card">
            <div className="tabs">
              <button className={`tab ${activeTab === 'deposit' ? 'active' : ''}`} onClick={() => setActiveTab('deposit')}>Deposit</button>
              <button className={`tab ${activeTab === 'withdraw' ? 'active' : ''}`} onClick={() => setActiveTab('withdraw')}>Withdraw</button>
            </div>
            <div className="card-body">
              {activeTab === 'deposit' && (
                <>
                  <div className="field-group"><label>Token</label><div className="select-display"><MonadLogo /><span>MONAD</span></div></div>
                  <div className="field-group">
                    <label>Amount</label>
                    <div className="amount-selector"><div className="amount-track">
                      {AMOUNTS.map((amt, idx) => (
                        <React.Fragment key={amt}>
                          <button className={`amount-node ${selectedAmount === amt ? 'active' : ''}`} onClick={() => setSelectedAmount(amt)}>
                            <div className="node-circle"></div><span className="node-label">{formatAmount(amt)} MON</span>
                          </button>
                          {idx < AMOUNTS.length - 1 && <div className="track-line"></div>}
                        </React.Fragment>
                      ))}
                    </div></div>
                  </div>
                  {!account ? <button className="primary-btn" onClick={connectWallet}>Connect Wallet</button> : <button className="primary-btn" onClick={handleDeposit} disabled={loading}>{loading ? 'Processing...' : `Deposit ${formatAmount(selectedAmount)} MON`}</button>}
                  <p className="fee-note">Protocol fee: 1%</p>
                  {note && (
                    <div className="note-section">
                      <div className="note-header"><span className="note-icon">🔐</span><strong>Your Private Note</strong></div>
                      <div className="note-warning-box"><p>⚠️ <strong>CRITICAL:</strong> Save this note now!</p><p>Without this note, your funds CANNOT be recovered!</p></div>
                      <textarea className="note-display" value={note} readOnly rows="4" />
                      <div className="note-actions">
                        <button className="secondary-btn" onClick={() => { copyNoteToClipboard(note); setMessage('📋 Note copied!'); }}>📋 Copy</button>
                        <button className="secondary-btn" onClick={() => downloadNote(note, selectedAmount)}>💾 Download</button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {activeTab === 'withdraw' && (
                <>
                  <div className="field-group"><label>Note</label><textarea className="input-field" value={withdrawNote} onChange={(e) => setWithdrawNote(e.target.value)} placeholder="Paste your private note (ghost-monad-...)" rows="3" /></div>
                  <div className="field-group"><label>Recipient Address</label><input className="input-field" type="text" value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)} placeholder="0x... (can be any address)" /></div>
                  {!account ? <button className="primary-btn" onClick={connectWallet}>Connect Wallet</button> : <button className="primary-btn" onClick={handleWithdraw} disabled={loading || !withdrawNote || !recipientAddress}>{loading ? 'Processing...' : 'Withdraw'}</button>}
                  {withdrawStatus && <div className="withdraw-status"><span className="spinner">⏳</span> {withdrawStatus}</div>}
                  <p className="fee-note">Protocol fee: 1% (deducted from withdrawal)</p>
                </>
              )}
              {message && <div className={`message ${message.includes('✅') ? 'success' : message.includes('❌') ? 'error' : 'info'}`}>{message}</div>}
            </div>
          </div>
          <div className="card stats-card">
            <h3>Pool Statistics</h3>
            <div className="stat-item"><span className="stat-label">Selected Pool</span><span className="stat-value">{formatAmount(selectedAmount)} MON</span></div>
            <div className="stat-item"><span className="stat-label">Anonymity Set</span><span className="stat-value">{stats.anonymitySet} deposits</span></div>
            <div className="stat-item"><span className="stat-label">Pool Balance</span><span className="stat-value">{parseFloat(stats.poolBalance).toFixed(2)} MON</span></div>
            {stats.anonymitySet === 0 && <p className="no-deposits">No deposits yet. Be the first!</p>}
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-left"><p>$Ghost CA: <span className="address">{GHOST_ADDRESS}</span></p><p>Ghost Protocol v{VERSION}</p></div>
        <div className="footer-right">
          <a href="https://x.com/GhostProtocolHQ" target="_blank" rel="noopener noreferrer" className="social-icon"><XLogo /></a>
          <a href="https://t.me/GhostProtocolHQ" className="social-icon"><TelegramIcon /></a>
        </div>
      </footer>

      {showComplianceModal && (
        <div className="modal-overlay" onClick={() => { setShowComplianceModal(false); setComplianceStatus(''); setComplianceCertificate(null); setComplianceNote(''); }}>
          <div className="modal compliance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔐 Compliance Tool</h2>
              <button className="modal-close" onClick={() => { setShowComplianceModal(false); setComplianceStatus(''); setComplianceCertificate(null); setComplianceNote(''); }}>×</button>
            </div>
            <div className="modal-body">
              <p>Generate a compliance certificate to prove the source of your funds. This creates an on-chain record linking your wallet to a deposit.</p>
              {!complianceCertificate ? (
                <>
                  <div className="field-group"><label>Your Deposit Note</label><textarea className="input-field" value={complianceNote} onChange={(e) => setComplianceNote(e.target.value)} placeholder="Paste your private note (ghost-monad-...)..." rows="3" /></div>
                  {complianceStatus && <div className={`compliance-status ${complianceStatus.includes('✅') ? 'success' : complianceStatus.includes('❌') ? 'error' : ''}`}>{complianceStatus}</div>}
                  <button className="primary-btn" onClick={handleGenerateCompliance} disabled={!complianceNote || !account}>Generate Compliance Certificate</button>
                  {!account && <p className="connect-notice">Connect your wallet to generate a compliance report</p>}
                </>
              ) : (
                <>
                  <div className="certificate-preview">
                    <h3>✅ Certificate Generated!</h3>
                    <p>Your compliance certificate has been created and recorded on-chain.</p>
                    <textarea className="certificate-display" value={complianceCertificate} readOnly rows="12" />
                  </div>
                  <div className="certificate-actions">
                    <button className="secondary-btn" onClick={() => { navigator.clipboard.writeText(complianceCertificate); setComplianceStatus('📋 Certificate copied!'); }}>📋 Copy Certificate</button>
                    <button className="primary-btn" onClick={() => { const noteData = parseNote(complianceNote); downloadComplianceCertificate(complianceCertificate, noteData.denomination); }}>💾 Download Certificate</button>
                  </div>
                  <button className="secondary-btn full-width" onClick={() => { setComplianceCertificate(null); setComplianceNote(''); setComplianceStatus(''); }}>Generate Another Report</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showWelcomeModal && (
        <div className="modal-overlay welcome-overlay">
          <div className="modal welcome-modal" onClick={(e) => e.stopPropagation()}>
            <div className="welcome-header">
              <img src="ghost.png" alt="Ghost Protocol" className="welcome-logo" />
              <h1>Welcome to Ghost Protocol</h1>
              <p className="welcome-subtitle">Privacy-Preserving Transactions on Monad</p>
            </div>
            <div className="welcome-body">
              <div className="welcome-section">
                <h3>🔒 What is Ghost Protocol?</h3>
                <p>Ghost Protocol is a <strong>privacy mixer</strong> that breaks the on-chain link between your deposit and withdrawal addresses using zero-knowledge proofs.</p>
              </div>
              
              <div className="welcome-section">
                <h3>🔄 How It Works</h3>
                <ul className="welcome-features">
                  <li><strong>Deposit</strong> — Deposit MON into a pool and receive a secret note</li>
                  <li><strong>Wait</strong> — Let your deposit mix with others in the pool</li>
                  <li><strong>Withdraw</strong> — Withdraw to ANY address with no traceable link</li>
                </ul>
              </div>

              <div className="welcome-section">
                <h3>✨ Why Use Ghost Protocol?</h3>
                <ul className="welcome-features">
                  <li>💰 <strong>Financial Privacy</strong> — Your transactions, your business</li>
                  <li>🛡️ <strong>Security</strong> — Protect yourself from targeted attacks</li>
                  <li>⚡ <strong>Fast & Cheap</strong> — Built on Monad with ~1 second blocks</li>
                  <li>📜 <strong>Compliant</strong> — Optional compliance tool for tax/legal needs</li>
                </ul>
              </div>

              <div className="welcome-note">
                <p>⚠️ <strong>Important:</strong> Always save your deposit note! Without it, your funds cannot be recovered.</p>
              </div>
            </div>
            <div className="welcome-footer">
              <button className="primary-btn welcome-btn" onClick={dismissWelcome}>
                🚀 Get Started
              </button>
              <p className="welcome-hint">This message won't appear again</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
